/**
 * Fixed CRUD Patterns for RTK Query
 *
 * This file contains common patterns and utilities for creating
 * consistent CRUD operations across the API layer.
 */

import { EndpointBuilder } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';

/** Properly typed EndpointBuilder matching the project's baseQueryWithRetry signature. */
type AppEndpointBuilder = EndpointBuilder<
  BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
  string,
  string
>;

// Tag types from baseApi - ensures type safety
export type ApiTag = 'Auth' | 'Conversations' | 'Messages' | 'TeamMembers' | 'Membership' | 'Tags' | 'User' | 'Organization' | 'Contacts' | 'ContactsCount' | 'ContactsInFolder' | 'ContactAssignments' | 'Folders' | 'Notes' | 'Notifications' | 'NotificationCount' | 'NotificationSettings' | 'PushSubscriptions' | 'UserProfile' | 'HelpSubmissions' | 'BulkCampaigns' | 'BulkCampaignProgress' | 'PhoneNumbers' | 'Settings' | 'ArchiveLogs' | 'ArchivedConversations' | 'ArchivedContacts' | 'Exports' | 'ExportLogs' | 'ExportStatus' | 'Presence' | 'PresenceState' | 'OnboardingSession' | 'Subscription' | 'Invoices' | 'Usage' | 'HawksoftIntegration' | 'HawksoftStatus' | 'Integrations' | 'IntegrationsStatus' | 'TeamConversations' | 'PartnerCredentials' | 'TenDlc' | 'NumberRegistration' | 'Templates' | 'ApprovedNumber' | 'TwilioPhoneNumber' | 'TierConfiguration' | 'Packages' | 'Subscriptions' | 'BillingAccount' | 'UsageHistory' | 'AuditLog' | 'Guardrails' | 'ContactPhoneNumber' | 'PlatformAdmins' | 'ExternalRequests' | 'PendingOrgs' | 'EmailTemplates' | 'PlatformOrganizations' | 'PlatformOrgDetail' | 'PlatformOrgUsers' | 'PlatformOrgIntegrations' | 'PlatformOrgBillingAccount' | 'PlatformOrgProjectedBilling' | 'PlatformOrgSubscriptions' | 'PlatformOrgUsage' | 'PlatformOrgTwilio' | 'PlatformOrgTenDlc' | 'PlatformOrgNumberReg' | 'PlatformOrgApprovedNumbers' | 'Widget';

// COMMON TYPES

export interface ApiResponse<T = unknown> {
  data: T;
  success?: boolean;
  status?: string;
  statusCode?: number;
  message?: string;
}

export interface ListApiResponse<T = unknown> extends ApiResponse<T[]> {
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
}

export interface PaginatedApiResponse<T = unknown> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Request types
export interface GetListArgs {
  page?: number | null;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  filter?: Record<string, unknown>;
  search?: string;
}

export interface CreateArgs<T> {
  data: T;
}

export interface UpdateArgs<T> {
  id: string;
  data: Partial<T>;
}

export interface DeleteArgs {
  id: string;
}

export interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// RESPONSE TRANSFORMERS

/**
 * Unwrap standard API response structure.
 *
 * Backend returns `{ success: true, data: T }` via successEnvelope.
 */
export const unwrapApiResponse = <T>(response: ApiResponse<T>): T => {
  if (response?.data !== undefined) {
    return response.data;
  }
  // Fallback: response is already the unwrapped shape (no envelope)
  return response as unknown as T;
};

/**
 * Unwrap list API response.
 *
 * Backend returns either `{ success, data: T[] }` (flat) or
 * `{ success, data: { data: T[], total, ... } }` (paginated).
 * This function extracts the data array from both shapes.
 */
export const unwrapListResponse = <T>(response: ListApiResponse<T> | T[]): T[] => {
  // Already an array — return directly
  if (Array.isArray(response)) {
    return response;
  }

  // Flat envelope: { data: T[] }
  if (Array.isArray(response.data)) {
    return response.data;
  }

  // Paginated envelope: { data: { data: T[], total, ... } }
  if (response.data && typeof response.data === 'object') {
    const inner = (response.data as Record<string, unknown>).data;
    if (Array.isArray(inner)) return inner as T[];
  }

  return [];
};

/**
 * Unwrap paginated API response
 */
export const unwrapPaginatedResponse = <T>(response: PaginatedApiResponse<T>) => ({
  data: response.data || [],
  meta: {
    total: response.total || 0,
    page: response.page || 1,
    limit: response.limit || 10,
    totalPages: response.totalPages || 1
  }
});

// TAG GENERATORS

/**
 * Generate provide tags for list endpoints
 */
export const generateListProvideTags = <T extends { id: string }>(
  tagType: ApiTag,
  result?: T[],
  listId = 'LIST'
) => {
  if (!result) {
    return [{ type: tagType, id: listId }];
  }

  // Ensure result is an array
  if (!Array.isArray(result)) {
    return [{ type: tagType, id: listId }];
  }

  return [
    { type: tagType, id: listId },
    ...result.map(item => ({ type: tagType, id: item.id }))
  ];
};

/**
 * Generate provide tags for single item endpoints
 */
export const generateItemProvideTags = (tagType: ApiTag, id: string | number) => [
  { type: tagType, id }
];

/**
 * Generate invalidate tags for mutations
 */
export const generateInvalidateTags = (tagType: ApiTag, listId = 'LIST') => [
  { type: tagType, id: listId }
];

// URL BUILDERS

/**
 * Build URL with query parameters
 */
export const buildUrl = (baseUrl: string, params?: GetListArgs): string => {
  if (!params || Object.keys(params).length === 0) {
    return baseUrl;
  }

  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (typeof value === 'object') {
        searchParams.append(key, JSON.stringify(value));
      } else {
        searchParams.append(key, String(value));
      }
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

// CRUD ENDPOINT CREATORS

/**
 * Create a standard GET list query
 */
export const createListQuery = <T extends { id: string }>(
  baseUrl: string,
  tagType: ApiTag,
  listId = 'LIST'
   
) => (builder: AppEndpointBuilder) => builder.query<T[], GetListArgs | void>({
  query: (params?: GetListArgs) => buildUrl(baseUrl, params),
  transformResponse: unwrapListResponse<T>,
  providesTags: (result?: T[]) => generateListProvideTags(tagType, result, listId)
});

/**
 * Create a standard GET by ID query
 */
export const createGetByIdQuery = <T>(
  baseUrl: string,
  tagType: ApiTag
   
) => (builder: AppEndpointBuilder) => builder.query<T, string>({
  query: (id: string) => `${baseUrl}/${id}`,
  transformResponse: unwrapApiResponse<T>,
  providesTags: (result: T | undefined, error: unknown, id: string) => generateItemProvideTags(tagType, id)
});

/**
 * Create a standard POST mutation
 */
export const createCreateMutation = <T, TArgs = CreateArgs<Partial<T>>>(
  baseUrl: string,
  tagType: ApiTag,
  listId = 'LIST'
   
) => (builder: AppEndpointBuilder) => builder.mutation<T, TArgs>({
  query: (args: TArgs) => ({
    url: baseUrl,
    method: 'POST' as const,
    body: 'data' in (args as object) ? (args as CreateArgs<Partial<T>>).data : args
  }),
  transformResponse: unwrapApiResponse<T>,
  invalidatesTags: generateInvalidateTags(tagType, listId)
});

/**
 * Create a standard PUT mutation
 */
export const createUpdateMutation = <T, TArgs = UpdateArgs<T>>(
  baseUrl: string,
  tagType: ApiTag,
  listId = 'LIST'
   
) => (builder: AppEndpointBuilder) => builder.mutation<T, TArgs>({
  query: (args: TArgs) => {
    const { id, data } = args as UpdateArgs<T>;
    return {
      url: `${baseUrl}/${id}`,
      method: 'PUT' as const,
      body: data
    };
  },
  transformResponse: unwrapApiResponse<T>,
  invalidatesTags: (result: T | undefined, error: unknown, args: TArgs) => [
    { type: tagType, id: (args as UpdateArgs<T>).id },
    { type: tagType, id: listId }
  ]
});

/**
 * Create a standard DELETE mutation
 */
export const createDeleteMutation = <T = void>(
  baseUrl: string,
  tagType: ApiTag,
  listId = 'LIST'
   
) => (builder: AppEndpointBuilder) => builder.mutation<T, DeleteArgs | string>({
  query: (args: DeleteArgs | string) => {
    const id = typeof args === 'string' ? args : args.id;
    return {
      url: `${baseUrl}/${id}`,
      method: 'DELETE' as const
    };
  },
  transformResponse: unwrapApiResponse<T>,
  invalidatesTags: (result: T | undefined, error: unknown, args: DeleteArgs | string) => {
    const id = typeof args === 'string' ? args : args.id;
    return [
      { type: tagType, id },
      { type: tagType, id: listId }
    ];
  }
});

/**
 * Create a nested list query (e.g., messages in a conversation)
 */
export const createNestedListQuery = <T extends { id: string }>(
  baseUrl: string,
  tagType: ApiTag,
  parentKey = 'parentId'
   
) => (builder: AppEndpointBuilder) => builder.query<T[], { parentId: string } & GetListArgs>({
  query: (params: { parentId: string } & GetListArgs) => {
    const { parentId, ...queryParams } = params;
    const url = `${baseUrl}/${parentId}`;
    return buildUrl(url, queryParams);
  },
  transformResponse: unwrapListResponse<T>,
  providesTags: (result: T[] | undefined, error: unknown, params: { parentId: string } & GetListArgs) =>
    result
      ? [
          { type: tagType, id: `${parentKey}-${params.parentId}` },
          ...result.map(item => ({ type: tagType, id: item.id }))
        ]
      : [{ type: tagType, id: `${parentKey}-${params.parentId}` }]
});

/**
 * Create a nested resource create mutation
 */
export const createNestedCreateMutation = <T>(
  baseUrl: string,
  tagType: ApiTag,
  parentKey = 'parentId'
   
) => (builder: AppEndpointBuilder) => builder.mutation<T, { parentId: string; data: unknown }>({
  query: ({ parentId, data }) => ({
    url: `${baseUrl}/${parentId}`,
    method: 'POST',
    body: data
  }),
  transformResponse: unwrapApiResponse<T>,
  invalidatesTags: (result: T | undefined, error: unknown, { parentId }) => [
    { type: tagType, id: `${parentKey}-${parentId}` },
    { type: tagType, id: 'LIST' }
  ]
});

/**
 * Create a paginated list query
 */
export const createPaginatedListQuery = <T extends { id: string }>(
  baseUrl: string,
  tagType: string
   
) => (builder: AppEndpointBuilder) => builder.query<{ data: T[], meta: PaginationMeta }, GetListArgs>({
  query: (params: GetListArgs) => buildUrl(baseUrl, params),
  transformResponse: unwrapPaginatedResponse<T>,
  providesTags: (result) =>
    result?.data
      ? [
          { type: tagType, id: 'PAGINATED-LIST' },
          ...result.data.map(item => ({ type: tagType, id: item.id }))
        ]
      : [{ type: tagType, id: 'PAGINATED-LIST' }]
});

/**
 * Create an infinite scroll query
 */
export const createInfiniteScrollQuery = <T extends { id: string }>(
  baseUrl: string,
  tagType: string
   
) => (builder: AppEndpointBuilder) => builder.query<{ data: T[], meta: PaginationMeta }, GetListArgs>({
  query: (params: GetListArgs) => buildUrl(baseUrl, params),
  transformResponse: unwrapPaginatedResponse<T>,
  // Merge incoming data with existing data for infinite scroll
  serializeQueryArgs: ({ endpointName }) => endpointName,
  merge: (currentCache, newItems) => {
    if (newItems.meta.page === 1) {
      return newItems;
    }
    return {
      ...newItems,
      data: [...currentCache.data, ...newItems.data]
    };
  },
  forceRefetch: ({ currentArg, previousArg }) => currentArg !== previousArg,
  providesTags: (result) =>
    result?.data
      ? [
          { type: tagType, id: 'INFINITE-LIST' },
          ...result.data.map(item => ({ type: tagType, id: item.id }))
        ]
      : [{ type: tagType, id: 'INFINITE-LIST' }]
});

/**
 * Create a search query
 */
export const createSearchQuery = <T extends { id: string }>(
  baseUrl: string,
  tagType: string
   
) => (builder: AppEndpointBuilder) => builder.query<T[], { search: string } & GetListArgs>({
  query: (params: { search: string } & GetListArgs) => buildUrl(baseUrl, params),
  transformResponse: unwrapListResponse<T>,
  providesTags: (result?: T[]) =>
    result && Array.isArray(result) && result.length > 0
      ? [
          { type: tagType, id: 'SEARCH' },
          ...result.map(item => ({ type: tagType, id: item.id }))
        ]
      : [{ type: tagType, id: 'SEARCH' }]
});

// NOTE: All functions above are individually exported (named exports).